import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useCompleteTutorial } from "../api/useCompleteTutorial";
import { getTutorialSteps } from "../lib/getTutorialSteps";
import { useTutorialStore } from "../model/store/useTutorialStore";

import "./tutorial.css";

import { useCampaignFormStore } from "@/entities/campaign/model/store/useCampaignFormStore";
import { useSessionStore } from "@/entities/session/models/store/useSessionStore";
import { ROUTES } from "@/shared/config";

/**
 * Onboarding tour of the dashboard.
 * Starts automatically on the first visit (tutorial_completed === false)
 * and can be reopened manually via useTutorialStore().open().
 */
export const Tutorial = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const accountInfo = useSessionStore((state) => state.accountInfo);
    const isOpen = useTutorialStore((state) => state.isOpen);
    const { mutate: completeTutorial } = useCompleteTutorial();
    const autoStartCheckedRef = useRef(false);

    useEffect(() => {
        if (autoStartCheckedRef.current || !accountInfo?.id) return;

        autoStartCheckedRef.current = true;
        if (!accountInfo.tutorial_completed) useTutorialStore.getState().open();
    }, [accountInfo]);

    useEffect(() => {
        if (!isOpen) return;

        let disposed = false;

        const finish = (afterFinish?: () => void) => {
            tour.destroy();
            afterFinish?.();
        };

        const tour = driver({
            showProgress: true,
            allowClose: true,
            overlayClickBehavior: () => undefined,
            stagePadding: 6,
            stageRadius: 8,
            popoverClass: "tutorial-popover",
            nextBtnText: t("tutorial.buttons.next"),
            prevBtnText: t("tutorial.buttons.back"),
            doneBtnText: t("tutorial.buttons.done"),
            progressText: t("tutorial.progress", {
                current: "{{current}}",
                total: "{{total}}",
            }),
            steps: getTutorialSteps({
                t,
                onSkip: () => finish(),
                onAddFunds: () => finish(() => navigate(ROUTES.FINANCES)),
                onCreateCampaign: () =>
                    finish(() => {
                        useCampaignFormStore.getState().flush();
                        navigate("/campaigns/create");
                    }),
            }),
            onDestroyed: () => {
                // Unmount (logout, etc.) is not a user's decision to finish the tour
                if (disposed) return;

                useTutorialStore.getState().close();
                if (!useSessionStore.getState().accountInfo?.tutorial_completed) {
                    completeTutorial();
                }
            },
        });

        tour.drive();

        return () => {
            disposed = true;
            if (tour.isActive()) tour.destroy();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    return null;
};
